/*
 * sale-now-price.js
 *
 * Patches Blockshop theme's <product-price-element> to handle the Sale-Now
 * campaign-driven pricing treatment on variant change. Sale-Now is a display
 * feature that previews Shopify's automatic discount at the price level, so
 * the customer sees the final-they-pay price before the cart.
 *
 * Loads alongside assets/promo-now-price.js. Both patch the same custom
 * element class. Order of patches: Promo NOW runs first (loaded earlier in
 * theme.liquid), Sale-Now runs second, wraps Promo NOW's wrapper.
 *
 * State resolver (locked in roadmap):
 *   1. Neither eligible: native price (no patch action)
 *   2. Promo NOW only: Promo NOW's patch handles it (this file passes through)
 *   3. Sale-Now only: this file renders .sale-now block, clears any .promo-now
 *   4. Both eligible (Promo NOW + Sale-Now overlap): this file renders .sale-now
 *      with a state-4 overlap note showing total savings from MSRP. Promo NOW
 *      markup is intentionally suppressed (one treatment, one source of truth).
 *
 * Math:
 *   final_sale_cents = variant.price * (100 - campaign_percent) / 100  (rounded)
 *   display_compare_cents:
 *     state 4 -> Lambda snapshot custom.promo_original_compare_at (MSRP)
 *     state 3 -> variant.compare_at_price if > variant.price, else variant.price
 *   total_off_percent for overlap note:
 *     (display_compare_cents - final_sale_cents) * 100 / display_compare_cents
 *     Floored intentionally (matches Liquid integer division in sale-now.liquid).
 *
 * Public API:
 *   window.SaleNow.buildMarkup, isEligible, resolveCompareCents, calcSalePrice,
 *   calcTotalOffPercent, formatMoney, parseTags
 *
 * Reuse for cart recommendations: window.SaleNow.buildMarkup is structured
 * the same way as window.PromoNow.buildMarkup so cart-recommendations-slider.js
 * can render server-equivalent .sale-now blocks after enriching variant data
 * with campaign metaobject values.
 */

(function() {
  'use strict';

  // ----------------------------------------------------------------------
  // Configuration
  // ----------------------------------------------------------------------

  var TARGET_ELEMENT = 'product-price-element';
  var PROMO_TAG = 'promo-now';
  var GIFT_CARD_TAG = 'gift-card';
  var GIFT_CARD_TYPE = 'Gift Cards';
  var EXCLUDE_TAG = 'sale-now-exclude';
  var HOST_CLASS = 'has-sale-now';
  var PROMO_HOST_CLASS = 'has-promo-now'; // toggled off when Sale-Now takes over
  var PERCENT_SENTINEL = '__PERCENT__';

  // Match Promo NOW's retry timings for consistency.
  var RETRY_INTERVAL_MS = 100;
  var RETRY_MAX_ATTEMPTS = 50;

  // ----------------------------------------------------------------------
  // Money formatting (mirrors Promo NOW's helper for visual parity)
  // ----------------------------------------------------------------------

  /**
   * Format a price (in cents) using the theme's money format string.
   * @param {number} cents
   * @param {string} moneyFormat
   * @returns {string}
   */
  function formatMoney(cents, moneyFormat) {
    var safeCents = Number(cents);
    if (!Number.isFinite(safeCents)) safeCents = 0;

    if (!moneyFormat) {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(safeCents / 100);
      } catch (error) {
        return '$' + (safeCents / 100).toFixed(2);
      }
    }

    var decimals = moneyFormat.indexOf('no_decimals') !== -1 ? 0 : 2;
    var locale = moneyFormat.indexOf('comma') !== -1 ? 'de-DE' : 'en-US';
    var formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(safeCents / 100);

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, formatted);
  }

  // ----------------------------------------------------------------------
  // Data normalization helpers
  // ----------------------------------------------------------------------

  /**
   * Parse a comma-separated tag list into an array of trimmed tags.
   * @param {string|string[]} tagsInput
   * @returns {string[]}
   */
  function parseTags(tagsInput) {
    if (Array.isArray(tagsInput)) {
      return tagsInput.map(function(tag) { return String(tag || '').trim(); }).filter(Boolean);
    }
    if (!tagsInput) return [];
    var parts = String(tagsInput).split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var t = parts[i].trim();
      if (t) out.push(t);
    }
    return out;
  }

  /**
   * Parse product-price.liquid's variant ID -> MSRP cents map. Reuses the
   * same data attribute that Promo NOW reads (data-promo-now-variant-msrp-map)
   * because the Lambda snapshot data is the SAME data; Sale-Now state 4 needs
   * it for the strikethrough source, same as Promo NOW state 2.
   * @param {string} raw
   * @returns {Object<string, number>}
   */
  function parseVariantMsrpMap(raw) {
    if (!raw) return {};
    try {
      var parsed = JSON.parse(raw);
      var normalized = {};
      Object.keys(parsed || {}).forEach(function(key) {
        var cents = Number(parsed[key]);
        if (Number.isFinite(cents) && cents > 0) {
          normalized[String(key)] = Math.round(cents);
        }
      });
      return normalized;
    } catch (error) {
      console.warn('[sale-now] Invalid data-promo-now-variant-msrp-map JSON; ignoring map', error);
      return {};
    }
  }

  /**
   * Read the Promo NOW MSRP snapshot from an enriched variant object.
   * Reused naming intentional: the snapshot field comes from the same
   * Lambda regardless of which treatment consumes it.
   * @param {Object} variant
   * @returns {number}
   */
  function readVariantSnapshotCents(variant) {
    if (!variant) return 0;
    var direct = Number(variant.promo_original_compare_at);
    if (Number.isFinite(direct) && direct > 0) return Math.round(direct);
    var explicitCents = Number(variant.promo_original_compare_at_cents);
    if (Number.isFinite(explicitCents) && explicitCents > 0) return Math.round(explicitCents);
    return 0;
  }

  // ----------------------------------------------------------------------
  // Math helpers
  // ----------------------------------------------------------------------

  /**
   * Compute the Sale-Now final sale price in cents.
   * Mirrors snippets/sale-now.liquid:
   *   multiplier = 100 - campaign.percent_off
   *   sale_cents = variant.price | times: multiplier | divided_by: 100.0 | round
   * The | round filter rounds to nearest integer (half away from zero in Liquid).
   * Math.round in JS matches for positive integers.
   * @param {number} priceCents
   * @param {number} campaignPercent
   * @returns {number} Sale price in cents, or 0 if input is invalid.
   */
  function calcSalePrice(priceCents, campaignPercent) {
    if (typeof priceCents !== 'number' || !Number.isFinite(priceCents) || priceCents <= 0) return 0;
    if (typeof campaignPercent !== 'number' || !Number.isFinite(campaignPercent)) return 0;
    if (campaignPercent <= 0 || campaignPercent >= 100) return priceCents;
    var multiplier = 100 - campaignPercent;
    return Math.round(priceCents * multiplier / 100);
  }

  /**
   * Compute the total savings percent from MSRP to final sale price.
   * Mirrors snippets/sale-now.liquid:
   *   savings = display_compare_cents - sale_price_cents
   *   total_off = savings * 100 / display_compare_cents
   * Liquid's integer division floors, so we Math.floor in JS for parity.
   * Example: $30 MSRP -> $8 final = 73 (not 73.33% or 74%).
   * @param {number} displayCompareCents
   * @param {number} salePriceCents
   * @returns {number}
   */
  function calcTotalOffPercent(displayCompareCents, salePriceCents) {
    if (!displayCompareCents || displayCompareCents <= 0) return 0;
    if (typeof salePriceCents !== 'number' || salePriceCents < 0) return 0;
    if (salePriceCents >= displayCompareCents) return 0;
    var diff = displayCompareCents - salePriceCents;
    return Math.floor(diff * 100 / displayCompareCents);
  }

  /**
   * Resolve the visible strikethrough source for Sale-Now.
   * Mirrors snippets/sale-now.liquid resolver:
   *   State 4 (Promo NOW overlap): Lambda snapshot custom.promo_original_compare_at
   *     if greater than variant.price, else variant.compare_at_price fallback
   *   State 3 (Sale-Now alone): variant.compare_at_price if > variant.price,
   *     else variant.price itself (so strikethrough always renders during a
   *     campaign even on products without native compare-at)
   * @param {Object} ctx - Product-level context
   * @param {Object} variant - Shopify variant
   * @param {boolean} isPromoNowOverlap - true when product is Promo NOW + Sale-Now
   * @returns {number} display compare cents (always >= variant.price)
   */
  function resolveCompareCents(ctx, variant, isPromoNowOverlap) {
    if (!variant || typeof variant.price !== 'number' || variant.price <= 0) return 0;

    var priceCents = variant.price;

    if (isPromoNowOverlap) {
      var variantId = String(variant.id || '');
      var map = ctx && ctx.variantMsrpMap ? ctx.variantMsrpMap : {};
      var mapCents = variantId && Object.prototype.hasOwnProperty.call(map, variantId)
        ? Number(map[variantId])
        : 0;
      var snapshotCents = readVariantSnapshotCents(variant) || mapCents;

      if (Number.isFinite(snapshotCents) && snapshotCents > priceCents) {
        return Math.round(snapshotCents);
      }
      if (typeof variant.compare_at_price === 'number' && variant.compare_at_price > priceCents) {
        return variant.compare_at_price;
      }
      // Defensive: shouldn't happen given the upstream guard, but never
      // emit zero -- fall back to price itself (downstream guard handles).
      return priceCents;
    }

    // State 3: native compare-at if present and meaningful, else price itself
    if (typeof variant.compare_at_price === 'number' && variant.compare_at_price > priceCents) {
      return variant.compare_at_price;
    }
    return priceCents;
  }

  // ----------------------------------------------------------------------
  // Eligibility logic
  // Mirrors snippets/sale-now-eligible.liquid for VARIANT-LEVEL checks only.
  // Product-level exclusions (gift card, exclude tag, subscription-only, scope)
  // are pre-decided server-side and exposed via data-sale-now-product-eligible.
  // ----------------------------------------------------------------------

  /**
   * Variant-level eligibility check. Called after the host's
   * data-sale-now-product-eligible attribute confirms the product itself
   * is in scope. Only checks per-variant conditions that can change with
   * variant selection: availability, price > 0, sale_price < display_compare.
   * @param {Object} ctx
   * @param {Object} variant
   * @returns {boolean}
   */
  function isEligible(ctx, variant) {
    if (!ctx || !variant || !variant.id) return false;
    if (!ctx.productEligible) return false; // server pre-decision (gift card, exclude tag, scope, etc.)
    if (!ctx.campaignPercent || ctx.campaignPercent <= 0) return false;
    if (!variant.available) return false;
    if (typeof variant.price !== 'number' || variant.price <= 0) return false;

    // Compute hypothetical render to apply the same guard as the snippet:
    // if sale_price >= display_compare, the snippet returns blank.
    var isOverlap = isPromoNowOverlap(ctx, variant);
    var displayCompare = resolveCompareCents(ctx, variant, isOverlap);
    if (!displayCompare || displayCompare <= 0) return false;

    var salePrice = calcSalePrice(variant.price, ctx.campaignPercent);
    if (salePrice >= displayCompare) return false;
    if (salePrice <= 0) return false;

    return true;
  }

  /**
   * Determine whether this is state 4 (Promo NOW + Sale-Now overlap).
   * Mirrors snippets/sale-now.liquid:
   *   product.tags contains 'promo-now' AND
   *   variant.compare_at_price > variant.price AND variant.price > 0
   * @param {Object} ctx
   * @param {Object} variant
   * @returns {boolean}
   */
  function isPromoNowOverlap(ctx, variant) {
    if (!ctx || !variant) return false;
    var tags = Array.isArray(ctx.tags) ? ctx.tags : parseTags(ctx.tags || '');
    if (tags.indexOf(PROMO_TAG) === -1) return false;
    if (typeof variant.compare_at_price !== 'number') return false;
    if (variant.compare_at_price <= variant.price) return false;
    if (variant.price <= 0) return false;
    return true;
  }

  // ----------------------------------------------------------------------
  // Markup builder
  // Mirrors snippets/sale-now.liquid output. DOM order matters because the
  // card-context CSS uses `order:` properties to rearrange children. Keep
  // the source order: label, prefix?, price, compare, overlap-note?
  // ----------------------------------------------------------------------

  /**
   * @typedef {Object} SaleNowLocales
   * @property {string} label - Campaign label, e.g. "MEMORIAL DAY SAVINGS"
   * @property {string} qualifierText - Resolved qualifier, e.g. "sale"
   * @property {string} overlapTemplate - "Saving __PERCENT__% on this item"
   * @property {string} salePriceLabel - SR label "Sale price"
   * @property {string} comparePriceLabel - SR label "Original price"
   */

  function normalizeLocales(locales) {
    return {
      label: locales && locales.label ? locales.label : '',
      qualifierText: locales && locales.qualifierText ? locales.qualifierText : 'sale',
      overlapTemplate: locales && locales.overlapTemplate ? locales.overlapTemplate : ('Saving ' + PERCENT_SENTINEL + '% on this item'),
      salePriceLabel: locales && locales.salePriceLabel ? locales.salePriceLabel : 'Sale price',
      comparePriceLabel: locales && locales.comparePriceLabel ? locales.comparePriceLabel : 'Original price'
    };
  }

  /**
   * Build the .sale-now block as an HTMLDivElement.
   * @param {Object} variant - Shopify variant (price in cents)
   * @param {string} moneyFormat
   * @param {string} context - 'pdp' | 'card' | 'cart'
   * @param {SaleNowLocales} locales
   * @param {Object} saleCtx - Context with campaignPercent, tags, variantMsrpMap, etc.
   * @returns {HTMLDivElement}
   */
  function buildMarkup(variant, moneyFormat, context, locales, saleCtx) {
    var ctx = context || 'card';
    var safeLocales = normalizeLocales(locales);
    var campaignPercent = saleCtx && saleCtx.campaignPercent ? saleCtx.campaignPercent : 0;
    var isOverlap = isPromoNowOverlap(saleCtx || {}, variant);
    var displayCompareCents = resolveCompareCents(saleCtx || {}, variant, isOverlap);
    var salePriceCents = calcSalePrice(variant.price, campaignPercent);
    var stateNumber = isOverlap ? 4 : 3;

    var root = document.createElement('div');
    root.className = 'sale-now sale-now--' + ctx + (isOverlap ? ' sale-now--overlap' : '');
    root.setAttribute('data-sale-now', 'true');
    root.setAttribute('data-sale-now-context', ctx);
    root.setAttribute('data-sale-now-state', String(stateNumber));

    // Label (always rendered, may be empty string if merchant omitted)
    var label = document.createElement('div');
    label.className = 'sale-now__label';
    label.textContent = safeLocales.label;
    root.appendChild(label);

    // Price row: SR label + amount + qualifier
    var price = document.createElement('div');
    price.className = 'sale-now__price';

    var priceSr = document.createElement('span');
    priceSr.className = 'visually-hidden';
    priceSr.textContent = safeLocales.salePriceLabel;
    price.appendChild(priceSr);

    var amount = document.createElement('span');
    amount.className = 'sale-now__amount';
    amount.textContent = formatMoney(salePriceCents, moneyFormat);
    price.appendChild(amount);

    var qualifier = document.createElement('span');
    qualifier.className = 'sale-now__qualifier';
    qualifier.textContent = safeLocales.qualifierText;
    price.appendChild(qualifier);

    root.appendChild(price);

    // Strikethrough compare row
    var compare = document.createElement('div');
    compare.className = 'sale-now__compare';

    var compareSr = document.createElement('span');
    compareSr.className = 'visually-hidden';
    compareSr.textContent = safeLocales.comparePriceLabel;
    compare.appendChild(compareSr);

    var s = document.createElement('s');
    s.textContent = formatMoney(displayCompareCents, moneyFormat);
    compare.appendChild(s);

    root.appendChild(compare);

    // Overlap note for state 4
    if (isOverlap) {
      var totalOff = calcTotalOffPercent(displayCompareCents, salePriceCents);
      var note = document.createElement('div');
      note.className = 'sale-now__overlap-note';
      var pill = document.createElement('span');
      pill.className = 'sale-now__savings-pill';
      pill.textContent = safeLocales.overlapTemplate.replace(PERCENT_SENTINEL, String(totalOff));
      note.appendChild(pill);
      root.appendChild(note);
    }

    return root;
  }

  // ----------------------------------------------------------------------
  // PDP context extraction
  // ----------------------------------------------------------------------

  /**
   * @typedef {Object} SaleNowContext
   * @property {string[]} tags
   * @property {string} productType
   * @property {string} moneyFormat
   * @property {SaleNowLocales} locales
   * @property {Object<string, number>} variantMsrpMap
   * @property {number} campaignPercent
   * @property {boolean} productEligible
   */

  /**
   * @param {HTMLElement} host - The <product-price-element>
   * @returns {SaleNowContext}
   */
  function readHostContext(host) {
    var tagsAttr = host.getAttribute('data-product-tags') || '';
    var typeAttr = host.getAttribute('data-product-type') || '';
    var moneyFormat = host.getAttribute('data-money-format') || '';
    var variantMsrpMapAttr = host.getAttribute('data-promo-now-variant-msrp-map') || '{}';

    var percentRaw = host.getAttribute('data-sale-now-percent') || '';
    var campaignPercent = Number(percentRaw);
    if (!Number.isFinite(campaignPercent)) campaignPercent = 0;

    var productEligible = (host.getAttribute('data-sale-now-product-eligible') || '').trim() === 'true';

    var locales = {
      label: host.getAttribute('data-sale-now-label') || '',
      qualifierText: host.getAttribute('data-sale-now-qualifier-text') || 'sale',
      overlapTemplate: host.getAttribute('data-sale-now-overlap-template') || ('Saving ' + PERCENT_SENTINEL + '% on this item'),
      salePriceLabel: host.getAttribute('data-sale-now-sale-price-label') || 'Sale price',
      comparePriceLabel: host.getAttribute('data-sale-now-compare-price-label') || 'Original price'
    };

    return {
      tags: parseTags(tagsAttr),
      productType: typeAttr,
      moneyFormat: moneyFormat,
      locales: locales,
      variantMsrpMap: parseVariantMsrpMap(variantMsrpMapAttr),
      campaignPercent: campaignPercent,
      productEligible: productEligible
    };
  }

  // ----------------------------------------------------------------------
  // DOM operations: apply / clear Sale-Now markup on the host
  // ----------------------------------------------------------------------

  /**
   * Render or update the .sale-now block inside the host. If one already
   * exists, replace it; otherwise insert as the first child. ALWAYS clear
   * any Promo NOW markup AND remove the has-promo-now class, since Sale-Now
   * suppresses Promo NOW in state 4.
   * @param {HTMLElement} host
   * @param {Object} variant
   * @param {SaleNowContext} ctx
   */
  function applySaleMarkup(host, variant, ctx) {
    var existing = host.querySelector(':scope > .sale-now');
    var fresh = buildMarkup(variant, ctx.moneyFormat, 'pdp', ctx.locales, ctx);
    if (existing) {
      existing.replaceWith(fresh);
    } else {
      host.insertBefore(fresh, host.firstChild);
    }

    // Suppress Promo NOW markup if present (state 4 case). Sale-Now's
    // overlap note carries the Promo NOW context now; we don't want both.
    var promoExisting = host.querySelector(':scope > .promo-now');
    if (promoExisting) promoExisting.remove();
    host.classList.remove(PROMO_HOST_CLASS);

    host.classList.add(HOST_CLASS);
  }

  /**
   * Remove the .sale-now block from host and let downstream (Promo NOW or
   * native) handle rendering. Removes has-sale-now class so CSS lets the
   * native wrapper become visible again.
   * @param {HTMLElement} host
   */
  function clearSaleMarkup(host) {
    var existing = host.querySelector(':scope > .sale-now');
    if (existing) existing.remove();
    host.classList.remove(HOST_CLASS);
  }

  // ----------------------------------------------------------------------
  // Patch <product-price-element>.updatePrices
  // ----------------------------------------------------------------------

  /**
   * Patch the custom element so variant changes route through Sale-Now
   * logic when applicable. Important: this patch runs AFTER Promo NOW's
   * patch (Promo NOW loads first in theme.liquid), so we wrap Promo NOW's
   * already-patched updatePrices. Inside our handler, we either:
   *   - Render Sale-Now (and let Promo NOW's wrapper still run for native
   *     wrapper updates; CSS hides everything except .sale-now)
   *   - Clear Sale-Now and let Promo NOW's wrapper handle the variant
   *     (state 1 or state 2 path)
   *
   * The end result: theme.js native handler always runs (Promo NOW's patch
   * delegates to it), so .product-price--original / .product-price--compare
   * stay updated even when our markup is showing on top.
   *
   * @param {Function} ProductPrice
   */
  function patchClass(ProductPrice) {
    var origUpdatePrices = ProductPrice.prototype.updatePrices;
    if (typeof origUpdatePrices !== 'function') {
      console.warn('[sale-now] product-price-element has no updatePrices method; skipping patch');
      return;
    }

    if (ProductPrice.prototype.__saleNowPatched) return;
    ProductPrice.prototype.__saleNowPatched = true;

    ProductPrice.prototype.updatePrices = function(variant) {
      var ctx = readHostContext(this);

      // No campaign at all: nothing for us to do.
      if (!ctx.campaignPercent) {
        return origUpdatePrices.call(this, variant);
      }

      // Product not in Sale-Now scope (gift card, excluded, subscription-only,
      // out of scope). Clear our markup if any, delegate.
      if (!ctx.productEligible) {
        clearSaleMarkup(this);
        return origUpdatePrices.call(this, variant);
      }

      // Product in scope but this specific variant fails per-variant checks.
      // Clear our markup and let downstream (Promo NOW or native) handle it.
      if (!isEligible(ctx, variant)) {
        clearSaleMarkup(this);
        return origUpdatePrices.call(this, variant);
      }

      // Variant eligible: let downstream (Promo NOW patch + native theme.js)
      // run FIRST so the native wrapper updates, then render Sale-Now LAST so
      // it wins state 4 overlap and clears any .promo-now markup that Promo
      // NOW's patch may have re-inserted.
      //
      // Order matters here: origUpdatePrices is the Promo NOW-patched method
      // (Promo NOW loads earlier in theme.liquid), which calls the raw native
      // theme.js handler internally. If we called applySaleMarkup BEFORE
      // origUpdatePrices, Promo NOW's wrapper would see state 4 variant
      // eligibility, re-insert .promo-now, and re-add has-promo-now, undoing
      // our "Sale-Now wins" rule.
      var result = origUpdatePrices.call(this, variant);
      applySaleMarkup(this, variant, ctx);
      return result;
    };
  }

  // ----------------------------------------------------------------------
  // Boot: wait for the custom element to register, then patch
  // ----------------------------------------------------------------------

  function boot(attempt) {
    var ProductPrice = customElements.get(TARGET_ELEMENT);
    if (ProductPrice) {
      patchClass(ProductPrice);
      return;
    }
    if (attempt >= RETRY_MAX_ATTEMPTS) {
      console.warn('[sale-now] ' + TARGET_ELEMENT + ' did not register within ' +
        (RETRY_INTERVAL_MS * RETRY_MAX_ATTEMPTS / 1000) + 's; giving up');
      return;
    }
    setTimeout(function() { boot(attempt + 1); }, RETRY_INTERVAL_MS);
  }

  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined(TARGET_ELEMENT).then(function() {
      var ProductPrice = customElements.get(TARGET_ELEMENT);
      if (ProductPrice) patchClass(ProductPrice);
    }).catch(function() {
      boot(0);
    });
  } else {
    boot(0);
  }

  // ----------------------------------------------------------------------
  // PDP body class hook
  // ----------------------------------------------------------------------
  //
  // When a Sale-Now-eligible product loads on a PDP and a campaign is active,
  // add `has-active-sale-now` to <body>. Third-party app blocks that read
  // variant.price directly (Joy loyalty points calculator, etc.) display
  // pre-discount numbers because Sale-Now is intentionally a DISPLAY-ONLY
  // feature and never mutates variant.price. The body class is the CSS
  // hook that lets sale-now.css hide those app blocks for the duration of
  // the campaign on eligible products.
  //
  // PDP-scoped: only checks `.main-product--root` (the Blockshop PDP wrapper),
  // so collection pages, cart drawer, etc. don't trigger the hook even if
  // they contain <product-price-element> instances.
  //
  // The body class is set once at load. The product-eligible flag is
  // server-decided at the product level (not per-variant), so variant
  // switching does not need to re-toggle the class.

  function applyPdpBodyClass() {
    var pdpRoot = document.querySelector('.main-product--root');
    if (!pdpRoot) return;

    var ppe = pdpRoot.querySelector(TARGET_ELEMENT);
    if (!ppe) return;

    var productEligible = ppe.getAttribute('data-sale-now-product-eligible') === 'true';
    var hasActiveCampaign = !!ppe.getAttribute('data-sale-now-percent');

    if (productEligible && hasActiveCampaign) {
      document.body.classList.add('has-active-sale-now');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyPdpBodyClass);
  } else {
    applyPdpBodyClass();
  }

  // ----------------------------------------------------------------------
  // Public API for cart-recommendations-slider.js and other consumers
  // ----------------------------------------------------------------------

  window.SaleNow = window.SaleNow || {};
  window.SaleNow.buildMarkup = buildMarkup;
  window.SaleNow.isEligible = isEligible;
  window.SaleNow.isPromoNowOverlap = isPromoNowOverlap;
  window.SaleNow.resolveCompareCents = resolveCompareCents;
  window.SaleNow.calcSalePrice = calcSalePrice;
  window.SaleNow.calcTotalOffPercent = calcTotalOffPercent;
  window.SaleNow.formatMoney = formatMoney;
  window.SaleNow.parseTags = parseTags;

})();